import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  Video,
  Phone,
  ExternalLink,
  User,
  Send,
  Loader2,
  Check,
} from "lucide-react";
import { Rich } from "@/components/Rich";
import { withDefaults } from "@/lib/sections";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Read Calendly URL from env. If empty, fall back to internal booking form.
const RAW_CALENDLY = (process.env.REACT_APP_CALENDLY_URL || "").trim();
const HAS_CALENDLY = RAW_CALENDLY.startsWith("https://calendly.com/");

export default function Booking({ content }) {
  const c = withDefaults("booking", content);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error("Nombre y teléfono son obligatorios.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: name.trim(),
        phone: phone.trim(),
        notes: notes.trim() || null,
      });
      setSubmitted(true);
      toast.success("¡Listo! Te contactamos en menos de 24h.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "No se pudo agendar la auditoría.";
      toast.error(typeof msg === "string" ? msg : "Error al agendar.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="agendar"
      data-testid="section-booking"
      className="relative py-20 sm:py-28 border-t border-slate-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          {/* Left: copy */}
          <div className="lg:col-span-2">
            <div className="font-mono text-xs text-sky-400 mb-3">
              {c.eyebrow}
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
              <Rich text={c.title} />
            </h2>
            <p className="mt-4 text-slate-400 text-lg leading-relaxed">
              {c.subtitle}
            </p>

            <ul className="mt-8 space-y-4">
              <li
                data-testid="booking-bullet-1"
                className="flex items-start gap-3"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-400">
                  <Clock size={18} />
                </span>
                <div>
                  <div className="text-white font-medium">15 minutos</div>
                  <div className="text-sm text-slate-400">
                    Directo al grano. Sin venta dura.
                  </div>
                </div>
              </li>
              <li
                data-testid="booking-bullet-2"
                className="flex items-start gap-3"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-orange-500/10 border border-orange-500/30 text-orange-400">
                  <Video size={18} />
                </span>
                <div>
                  <div className="text-white font-medium">Por Google Meet</div>
                  <div className="text-sm text-slate-400">
                    Recibirás el enlace al confirmar.
                  </div>
                </div>
              </li>
              <li
                data-testid="booking-bullet-3"
                className="flex items-start gap-3"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-400">
                  <Phone size={18} />
                </span>
                <div>
                  <div className="text-white font-medium">
                    Solo Nombre y Teléfono
                  </div>
                  <div className="text-sm text-slate-400">
                    Cero spam. Cero llamadas frías.
                  </div>
                </div>
              </li>
            </ul>

            {HAS_CALENDLY && (
              <a
                href={RAW_CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-cta-external"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-semibold text-white bg-orange-500 hover:bg-orange-600 transition-all shadow-[0_0_25px_rgba(249,115,22,0.35)] hover:shadow-[0_0_35px_rgba(249,115,22,0.55)]"
              >
                <Calendar size={18} /> Abrir Calendly
                <ExternalLink size={14} />
              </a>
            )}
          </div>

          {/* Right: Calendly embed OR internal form */}
          <div className="lg:col-span-3 rounded-2xl border border-slate-800 bg-slate-900/60 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-slate-950/60">
              <div className="font-mono text-xs text-slate-500">
                {HAS_CALENDLY ? "calendar.divelopers.com" : "agendar.divelopers.com"}
              </div>
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
              </div>
            </div>

            {HAS_CALENDLY ? (
              <div
                data-testid="booking-calendly-embed"
                className="relative w-full"
                style={{ minHeight: "640px" }}
              >
                <iframe
                  title="Calendly - Auditoría Divelopers"
                  src={`${RAW_CALENDLY}?embed_domain=divelopers.com&embed_type=Inline&background_color=0f172a&text_color=e2e8f0&primary_color=f97316`}
                  className="absolute inset-0 w-full h-full border-0"
                  loading="lazy"
                />
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="booking-form"
                className="p-6 sm:p-8 space-y-5"
              >
                <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 p-3 text-xs text-sky-300 font-mono">
                  // calendly.no_configurado · usando agenda interna
                </div>

                <div>
                  <label className="font-mono text-xs text-sky-400">
                    01 / Nombre completo *
                  </label>
                  <div className="relative mt-2">
                    <User
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
                    />
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="booking-input-name"
                      placeholder="María González"
                      className="w-full pl-9 pr-4 py-3 rounded-lg bg-slate-950/60 border border-slate-800 text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-mono text-xs text-sky-400">
                    02 / Teléfono / WhatsApp *
                  </label>
                  <div className="relative mt-2">
                    <Phone
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
                    />
                    <input
                      type="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      data-testid="booking-input-phone"
                      placeholder="+52 55 1234 5678"
                      className="w-full pl-9 pr-4 py-3 rounded-lg bg-slate-950/60 border border-slate-800 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500/60 focus:ring-2 focus:ring-orange-500/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-mono text-xs text-sky-400">
                    03 / ¿Algo que debamos saber? (opcional)
                  </label>
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="booking-input-notes"
                    placeholder="Sitio actual, giro del negocio, horario preferido..."
                    className="mt-2 w-full px-4 py-3 rounded-lg bg-slate-950/60 border border-slate-800 text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/20 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting || submitted}
                  data-testid="booking-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_0_25px_rgba(249,115,22,0.35)] hover:shadow-[0_0_35px_rgba(249,115,22,0.55)]"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Agendando...
                    </>
                  ) : submitted ? (
                    <>
                      <Check size={18} /> ¡Solicitud recibida!
                    </>
                  ) : (
                    <>
                      <Send size={18} /> Reservar mi auditoría gratis
                    </>
                  )}
                </button>

                <p className="text-xs text-slate-500">
                  Te contactamos por WhatsApp en menos de 24 horas para
                  confirmar fecha y hora.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
